import { useMutation } from "@apollo/client";
import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useCallback, useMemo, useState } from "react";
import {
  GridContextProvider,
  GridDropZone,
  GridItem,
  swap,
} from "react-grid-dnd";

import { UPDATE_IMAGE_ORDER as updateImageOrderMutation } from "../../../../graphql/mutation/Image";
import useUser from "../../../../hooks/useUser";
import { Button, IconButton, Modal } from "../../../ui";
import DeleteImage from "./components/DeleteImage";
import MoveImage from "./components/MoveImage";
import UploadImage from "./components/UploadImage";

const GRID_DND_ITEM_IN_ROW = 4;
const GRID_DND_ROW_HEIGHT = 240;

const ManageImage = props => {
  const {
    images: propsImages = [],
    setImageList,
    setSuccessOpModal,
    moveAble = true,
    deleteAble = true,
    orderUpdateAble = true,
    artwork,
  } = props;

  const { hasPermission } = useUser();
  const [updateImageOrder, { loading: updateImageOrderLoading }] = useMutation(
    updateImageOrderMutation({ imgT: true, order: true }),
    { refetchQueries: ["FetchArtwork", "FetchInstance"] },
  );

  const [localImages, setLocalImages] = useState(propsImages);
  const [selectedImages, setSelectedImages] = useState([]);

  const enableUpdateOrderButton = useMemo(() => {
    let enable = false;
    localImages?.forEach((item, index) => {
      if (!enable) {
        enable = item.order !== index + 1;
      }
    });
    return enable;
  }, [localImages]);

  const resetLocalState = useCallback(() => {
    setSelectedImages([]);
    setLocalImages(propsImages);
  }, [propsImages]);

  const handleSelectImage = useCallback(
    archiveId => {
      if (!selectedImages.includes(archiveId)) {
        setSelectedImages(selected => [...selected, archiveId]);
      }
    },
    [selectedImages],
  );

  const handleUnselectImage = useCallback(
    archiveId => {
      if (selectedImages.includes(archiveId)) {
        setSelectedImages(selected => selected.filter(id => id !== archiveId));
      }
    },
    [selectedImages],
  );

  const removeUnLinkedImages = useCallback(() => {
    setImageList?.(prevState =>
      prevState.filter(item => !selectedImages.includes(item.archiveId)),
    );
    setLocalImages(prevState =>
      prevState.filter(item => !selectedImages.includes(item.archiveId)),
    );
  }, [selectedImages, setImageList]);

  const onDeleteSuccess = useCallback(() => {
    removeUnLinkedImages();
    setSuccessOpModal?.({
      isOpen: true,
      message: `${selectedImages.length} ${selectedImages.length > 1 ? "images" : "image"} deleted successfully.`,
    });
    resetLocalState();
  }, [
    removeUnLinkedImages,
    resetLocalState,
    selectedImages.length,
    setSuccessOpModal,
  ]);

  const onContextChange = useCallback((_, sourceIndex, targetIndex) => {
    setLocalImages(localImages => {
      return swap(localImages, sourceIndex, targetIndex);
    });
  }, []);

  const handleUpdateOrder = async e => {
    e.preventDefault();

    try {
      const res = await updateImageOrder({
        variables: {
          input: {
            newImageOrder: localImages.map((item, index) => ({
              artworkId: item.artworkId,
              archiveId: item.archiveId,
              order: index + 1,
            })),
          },
        },
      });
      const images = res?.data?.updateImageOrder.images;
      const success = res?.data?.updateImageOrder.success;

      if (success && images?.length > 0) {
        setImageList?.(images);
        setLocalImages(images);
      }
    } catch (err) {
      console.error("handleUpdateOrder", err);
    }
  };

  const onMoveSuccess = () => {
    removeUnLinkedImages();
    setSuccessOpModal?.({ isOpen: true, message: "Images has been moved." });
    resetLocalState();
  };

  const modalProps = {
    scale: "md",
    closeOnBackdrop: true,
    hideCloseButton: true,
    onOpen: () => {
      setLocalImages(propsImages);
    },
    onClose: resetLocalState,
    body: ({ closeModal }) => {
      const gridDnDRows = Math.ceil(localImages.length / GRID_DND_ITEM_IN_ROW);
      const gridDnDHeight = GRID_DND_ROW_HEIGHT * gridDnDRows;

      return (
        <div className="flex w-full flex-col p-8">
          <div className="flex flex-row">
            <div className="flex flex-1 flex-col text-2xl font-bold">
              Manage Artwork Images
            </div>
            <div className="flex flex-row gap-4">
              {hasPermission("CREATE_IMAGE") && (
                <UploadImage mainCloseModal={closeModal} artwork={artwork} />
              )}
              {hasPermission("ORDER_IMAGES") && orderUpdateAble && (
                <Button
                  className="flex !min-w-28 items-center"
                  label="Update Order"
                  loading={updateImageOrderLoading}
                  disabled={updateImageOrderLoading || !enableUpdateOrderButton}
                  onClick={handleUpdateOrder}
                />
              )}
              {moveAble && (
                <MoveImage
                  selectedImages={selectedImages}
                  artworkId={propsImages[0]?.artworkId}
                  onSuccess={onMoveSuccess}
                  mainCloseModal={closeModal}
                />
              )}
              {hasPermission("DELETE_IMAGE") && deleteAble && (
                <DeleteImage
                  selectedImages={selectedImages}
                  artworkId={propsImages[0]?.artworkId}
                  onSuccess={onDeleteSuccess}
                  mainCloseModal={closeModal}
                />
              )}
            </div>
          </div>

          <div className="mt-8">
            <div className="h-[500px] overflow-y-auto">
              {localImages?.length > 0 ? (
                <GridContextProvider onChange={onContextChange}>
                  <GridDropZone
                    id="items"
                    boxesPerRow={GRID_DND_ITEM_IN_ROW}
                    rowHeight={GRID_DND_ROW_HEIGHT}
                    style={{ height: `${gridDnDHeight}px` }}
                  >
                    {localImages?.map((item, index) => (
                      <GridItem key={item.archiveId}>
                        <div className="mb-4 h-full w-full p-4">
                          <div className="group relative h-full border-2 bg-gray-100 hover:cursor-pointer hover:border-black hover:bg-gray-300">
                            <div
                              className="h-full bg-contain bg-center bg-no-repeat"
                              style={{
                                backgroundImage: `url("${item.imgT}")`,
                              }}
                            />
                            <div
                              className={`invisible
                                 ${selectedImages.includes(item.archiveId) && "!visible"}
                                  absolute left-0 top-0 flex h-full w-full justify-center bg-black/[.5] group-hover:visible`}
                            >
                              {selectedImages.includes(item.archiveId) ? (
                                <IconButton variant="clean" title="Deselect">
                                  <XMarkIcon
                                    className={`h-8 w-8 cursor-pointer rounded-lg bg-white p-2`}
                                    onClick={() =>
                                      handleUnselectImage(item.archiveId)
                                    }
                                  />
                                </IconButton>
                              ) : (
                                <IconButton variant="clean" title="Select">
                                  <PlusIcon
                                    className={`h-8 w-8 cursor-pointer rounded-lg bg-white p-2`}
                                    onClick={() =>
                                      handleSelectImage(item.archiveId)
                                    }
                                  />
                                </IconButton>
                              )}
                            </div>
                          </div>
                          <div className="mt-2 flex items-center justify-between">
                            <div className="px-1 text-sm font-bold tracking-wide text-gray-600">
                              {index + 1}
                            </div>
                            <div className="px-1 text-sm tracking-wide text-gray-600">
                              {item.archiveId}
                            </div>
                          </div>
                        </div>
                      </GridItem>
                    ))}
                  </GridDropZone>
                </GridContextProvider>
              ) : (
                <div
                  className={`text-md flex h-full w-full items-center justify-center text-gray-600`}
                >
                  NO IMAGES TO SHOW
                </div>
              )}
            </div>
            {localImages.length > 0 && (
              <div
                className={`mt-5 w-full text-right text-lg font-bold tracking-wide text-gray-600`}
              >
                {selectedImages.length} of {localImages.length}{" "}
                <button
                  type="button"
                  disabled={selectedImages.length === 0}
                  onClick={() => setSelectedImages([])}
                  className={`inline align-baseline ${
                    selectedImages.length > 0
                      ? "cursor-pointer text-blue-600 underline hover:text-blue-700"
                      : "cursor-default text-gray-600 no-underline"
                  }`}
                >
                  selected
                </button>
              </div>
            )}
          </div>
        </div>
      );
    },
  };

  return (
    <Modal {...modalProps}>
      <Button label="Manage Images" className={"!w-32"} />
    </Modal>
  );
};

export default ManageImage;
